/* eslint-disable max-lines */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  RadioGroup,
  Radio,
  FormLabel,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Functions as FunctionsIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  ToggleOn as BooleanIcon,
  List as ListIcon,
  Code as CodeIcon
} from '@mui/icons-material';

const VARIABLE_TYPES = {
  entity_field: {
    label: 'Entity Data',
    icon: <BusinessIcon />,
    color: '#2196f3',
    shortDesc: 'Pull from entity records',
    description: 'Automatically pulls information from your entity records',
    examples: ['Entity name', 'Contact info', 'Status']
  },
  custom: {
    label: 'Custom Input',
    icon: <TextFieldsIcon />,
    color: '#9c27b0',
    shortDesc: 'User enters when generating',
    description: 'Fields where you enter specific information when creating a report',
    examples: ['Meeting date', 'Project budget', 'Custom notes']
  },
  system: {
    label: 'Smart Defaults',
    icon: <SettingsIcon />,
    color: '#00bcd4',
    shortDesc: 'System fills automatically',
    description: 'Automatically filled by the system',
    examples: ['Today\'s date', 'Page numbers', 'Your name']
  },
  computed: {
    label: 'Calculations',
    icon: <CodeIcon />,
    color: '#ff9800',
    shortDesc: 'Calculated from other variables',
    description: 'Automatically calculated based on other variables',
    examples: ['Total = income - expenses', 'Percentage complete']
  }
};

const VALUE_TYPES = {
  text: { label: 'Text', icon: <TextFieldsIcon /> },
  number: { label: 'Number', icon: <NumbersIcon /> },
  date: { label: 'Date', icon: <CalendarIcon /> },
  boolean: { label: 'Yes/No', icon: <BooleanIcon /> },
  select: { label: 'Select List', icon: <ListIcon /> }
};

const ENTITY_FIELDS = [
  { name: 'name', label: 'Entity Name', type: 'text' },
  { name: 'description', label: 'Description', type: 'text' },
  { name: 'type', label: 'Entity Type', type: 'text' },
  { name: 'status', label: 'Status', type: 'text' },
  { name: 'createdAt', label: 'Created Date', type: 'date' },
  { name: 'email', label: 'Email', type: 'text' },
  { name: 'phone', label: 'Phone', type: 'text' },
  { name: 'address', label: 'Address', type: 'text' },
  { name: 'website', label: 'Website', type: 'text' },
  { name: 'industry', label: 'Industry', type: 'text' }
];

const SYSTEM_VARIABLES = [
  { name: 'currentDate', label: 'Current Date', type: 'date', format: 'YYYY-MM-DD' },
  { name: 'currentDateTime', label: 'Current Date & Time', type: 'date', format: 'YYYY-MM-DD HH:mm:ss' },
  { name: 'reportDate', label: 'Report Date', type: 'date', format: 'YYYY-MM-DD' },
  { name: 'pageNumber', label: 'Page Number', type: 'number' },
  { name: 'totalPages', label: 'Total Pages', type: 'number' },
  { name: 'userName', label: 'Current User Name', type: 'text' },
  { name: 'userEmail', label: 'Current User Email', type: 'text' }
];

// eslint-disable-next-line max-lines-per-function
export default function VariableManager({
  variables = [],
  customVariables = [],
  onChange,
  onVariableInsert,
  _entityBranding
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [variableForm, setVariableForm] = useState({
    name: '',
    type: 'custom',
    entityField: '',
    valueType: 'text',
    description: '',
    required: false,
    defaultValue: '',
    format: '',
    computed: '',
    options: []
  });
  const [newOption, setNewOption] = useState('');

  // Group variables by type
  const groupedVariables = {
    entity_field: [],
    custom: [],
    system: [],
    computed: []
  };

  [...variables, ...customVariables].forEach(variable => {
    if (groupedVariables[variable.type]) {
      groupedVariables[variable.type].push(variable);
    }
  });

  const handleAddVariable = () => {
    setEditingVariable(null);
    setVariableForm({
      name: '',
      type: 'custom',
      entityField: '',
      valueType: 'text',
      description: '',
      required: false,
      defaultValue: '',
      format: '',
      computed: '',
      options: []
    });
    setDialogOpen(true);
  };

  const handleEditVariable = (variable) => {
    setEditingVariable(variable);
    setVariableForm({
      ...variable,
      valueType: variable.valueType || 'text',
      options: variable.options || []
    });
    setDialogOpen(true);
  };

  const handleDeleteVariable = (variable) => {
    if (window.confirm(`Delete variable "${variable.name}"?`)) {
      const updatedVariables = variables.filter(v => v.name !== variable.name);
      const updatedCustom = customVariables.filter(v => v.name !== variable.name);
      onChange(updatedVariables, updatedCustom);
    }
  };

  const handleSaveVariable = () => {
    if (!variableForm.name) {
      alert('Variable name is required');
      return;
    }

    const newVariable = {
      name: variableForm.name,
      type: variableForm.type,
      description: variableForm.description,
      required: variableForm.required
    };

    // Add type-specific fields
    if (variableForm.type === 'entity_field') {
      newVariable.entityField = variableForm.entityField;
    } else if (variableForm.type === 'custom') {
      newVariable.valueType = variableForm.valueType;
      newVariable.defaultValue = variableForm.defaultValue;
      if (variableForm.valueType === 'select') {
        newVariable.options = variableForm.options;
      }
      if (variableForm.valueType === 'date' || variableForm.valueType === 'number') {
        newVariable.format = variableForm.format;
      }
    } else if (variableForm.type === 'system') {
      const systemVar = SYSTEM_VARIABLES.find(v => v.name === variableForm.name);
      if (systemVar) {
        newVariable.format = systemVar.format;
        newVariable.defaultValue = systemVar.type === 'date' ? new Date().toISOString() : '';
      }
    } else if (variableForm.type === 'computed') {
      newVariable.computed = variableForm.computed;
    }

    // Update or add variable
    let updatedVariables = [...variables];
    let updatedCustom = [...customVariables];

    if (editingVariable) {
      // Remove old variable
      updatedVariables = updatedVariables.filter(v => v.name !== editingVariable.name);
      updatedCustom = updatedCustom.filter(v => v.name !== editingVariable.name);
    }

    // Add new/updated variable to appropriate array
    if (variableForm.type === 'custom') {
      updatedCustom.push(newVariable);
    } else {
      updatedVariables.push(newVariable);
    }

    onChange(updatedVariables, updatedCustom);
    setDialogOpen(false);
  };

  const handleAddOption = () => {
    if (newOption && !variableForm.options.includes(newOption)) {
      setVariableForm({
        ...variableForm,
        options: [...variableForm.options, newOption]
      });
      setNewOption('');
    }
  };

  const handleRemoveOption = (option) => {
    setVariableForm({
      ...variableForm,
      options: variableForm.options.filter(o => o !== option)
    });
  };

  // eslint-disable-next-line max-lines-per-function
  const renderVariableGroup = (type, variables) => {
    if (variables.length === 0) return null;

    const typeInfo = VARIABLE_TYPES[type];

    return (
      <Accordion defaultExpanded key={type}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: alpha(typeInfo.color, 0.04),
            '&:hover': {
              backgroundColor: alpha(typeInfo.color, 0.08)
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: typeInfo.color, display: 'flex' }}>
              {typeInfo.icon}
            </Box>
            <Typography fontWeight="medium">{typeInfo.label}</Typography>
            <Chip
              label={variables.length}
              size="small"
              sx={{
                backgroundColor: typeInfo.color,
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List>
            {variables.map((variable, index) => (
              <ListItem
                key={index}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip
                        label={`{{${variable.name}}}`}
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: alpha(typeInfo.color, 0.1),
                          borderColor: typeInfo.color,
                          fontWeight: 'medium'
                        }}
                        variant="outlined"
                      />
                      {variable.required && (
                        <Chip
                          label="Required"
                          size="small"
                          color="error"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      {variable.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {variable.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {variable.entityField && (
                          <Typography variant="caption" color="primary">
                            <strong>Field:</strong> {ENTITY_FIELDS.find(f => f.name === variable.entityField)?.label || variable.entityField}
                          </Typography>
                        )}
                        {variable.defaultValue && (
                          <Typography variant="caption" color="text.secondary">
                            <strong>Default:</strong> {variable.defaultValue}
                          </Typography>
                        )}
                        {variable.valueType && (
                          <Typography variant="caption" color="text.secondary">
                            <strong>Type:</strong> {VALUE_TYPES[variable.valueType]?.label || variable.valueType}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {onVariableInsert && (
                    <Tooltip title="Insert into template">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => onVariableInsert(variable)}
                        sx={{ mr: 1 }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(type === 'custom' || type === 'computed') && (
                    <>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleEditVariable(variable)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDeleteVariable(variable)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FunctionsIcon />
          Template Variables
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddVariable}
        >
          Add Variable
        </Button>
      </Box>
      {Object.keys(groupedVariables).some(type => groupedVariables[type].length > 0) ? (
        Object.entries(groupedVariables).map(([type, vars]) =>
          renderVariableGroup(type, vars)
        )
      ) : (
        <Alert severity="info">
          No variables defined yet. Add variables to make your template dynamic.
        </Alert>
      )}
      {/* Variable Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingVariable ? 'Edit Variable' : 'Add Variable'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Variables are placeholders like <strong>{'{{name}}'}</strong> that get replaced with real data when you generate a report.
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel sx={{ mb: 2, fontWeight: 'medium' }}>Choose Variable Type</FormLabel>
                <RadioGroup
                  value={variableForm.type}
                  onChange={(e) => setVariableForm({ ...variableForm, type: e.target.value })}
                >
                  <Grid container spacing={2}>
                    {Object.entries(VARIABLE_TYPES).map(([key, type]) => (
                      <Grid
                        key={key}
                        size={{
                          xs: 12,
                          sm: 6
                        }}>
                        <Card
                          variant={variableForm.type === key ? 'elevation' : 'outlined'}
                          sx={{
                            borderColor: variableForm.type === key ? type.color : 'divider',
                            borderWidth: variableForm.type === key ? 2 : 1,
                            backgroundColor: variableForm.type === key ? alpha(type.color, 0.04) : 'transparent',
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: type.color,
                              backgroundColor: alpha(type.color, 0.02)
                            }
                          }}
                          onClick={() => setVariableForm({ ...variableForm, type: key })}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <FormControlLabel
                              value={key}
                              control={
                                <Radio
                                  sx={{
                                    color: type.color,
                                    '&.Mui-checked': {
                                      color: type.color
                                    },
                                    p: 0,
                                    mr: 1
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box sx={{ color: type.color, display: 'flex' }}>
                                      {type.icon}
                                    </Box>
                                    <Typography variant="subtitle2" fontWeight="medium">
                                      {type.label}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                    {type.shortDesc}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {type.examples.slice(0, 2).map((ex, i) => (
                                      <Chip
                                        key={i}
                                        label={ex}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          height: 18,
                                          fontSize: '0.7rem',
                                          '& .MuiChip-label': { px: 1 }
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              }
                              sx={{ m: 0, width: '100%' }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>

            {variableForm.type === 'entity_field' ? (
              <>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel>Entity Field</InputLabel>
                    <Select
                      value={variableForm.entityField}
                      onChange={(e) => {
                        const field = ENTITY_FIELDS.find(f => f.name === e.target.value);
                        setVariableForm({
                          ...variableForm,
                          entityField: e.target.value,
                          name: e.target.value,
                          description: field?.label || ''
                        });
                      }}
                      label="Entity Field"
                    >
                      {ENTITY_FIELDS.map(field => (
                        <MenuItem key={field.name} value={field.name}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : variableForm.type === 'system' ? (
              <>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel>System Variable</InputLabel>
                    <Select
                      value={variableForm.name}
                      onChange={(e) => {
                        const sysVar = SYSTEM_VARIABLES.find(v => v.name === e.target.value);
                        setVariableForm({
                          ...variableForm,
                          name: e.target.value,
                          description: sysVar?.label || ''
                        });
                      }}
                      label="System Variable"
                    >
                      {SYSTEM_VARIABLES.map(variable => (
                        <MenuItem key={variable.name} value={variable.name}>
                          {variable.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : (
              <>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Variable Name"
                    value={variableForm.name}
                    onChange={(e) => setVariableForm({ ...variableForm, name: e.target.value.replace(/\s+/g, '_') })}
                    helperText="Use underscores instead of spaces"
                  />
                </Grid>

                {variableForm.type === 'custom' && (
                  <>
                    <Grid size={12}>
                      <FormControl fullWidth>
                        <InputLabel>Value Type</InputLabel>
                        <Select
                          value={variableForm.valueType}
                          onChange={(e) => setVariableForm({ ...variableForm, valueType: e.target.value })}
                          label="Value Type"
                        >
                          {Object.entries(VALUE_TYPES).map(([key, info]) => (
                            <MenuItem key={key} value={key}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {info.icon}
                                {info.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {variableForm.valueType === 'select' && (
                      <Grid size={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Options
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            size="small"
                            placeholder="Add option"
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                          />
                          <Button size="small" onClick={handleAddOption}>
                            Add
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {variableForm.options.map((option, index) => (
                            <Chip
                              key={index}
                              label={option}
                              onDelete={() => handleRemoveOption(option)}
                              size="small"
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {(variableForm.valueType === 'date' || variableForm.valueType === 'number') && (
                      <Grid size={12}>
                        <TextField
                          fullWidth
                          label="Format"
                          value={variableForm.format}
                          onChange={(e) => setVariableForm({ ...variableForm, format: e.target.value })}
                          helperText={
                            variableForm.valueType === 'date'
                              ? 'e.g., YYYY-MM-DD, MM/DD/YYYY'
                              : 'e.g., #,##0.00'
                          }
                        />
                      </Grid>
                    )}

                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Default Value"
                        value={variableForm.defaultValue}
                        onChange={(e) => setVariableForm({ ...variableForm, defaultValue: e.target.value })}
                      />
                    </Grid>
                  </>
                )}

                {variableForm.type === 'computed' && (
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Formula"
                      value={variableForm.computed}
                      onChange={(e) => setVariableForm({ ...variableForm, computed: e.target.value })}
                      helperText="JavaScript expression using other variables"
                      placeholder="e.g., {{revenue}} - {{expenses}}"
                    />
                  </Grid>
                )}
              </>
            )}

            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                value={variableForm.description}
                onChange={(e) => setVariableForm({ ...variableForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={variableForm.required}
                    onChange={(e) => setVariableForm({ ...variableForm, required: e.target.checked })}
                  />
                }
                label="Required Variable"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveVariable} variant="contained">
            {editingVariable ? 'Update' : 'Add'} Variable
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}